import React, { useEffect, useState } from 'react'
import LocationFilters from '../components/LocationFilters'
import Search from '../components/Search'
import SortControls from '../components/SortControls'
import ContentGrid from '../components/ContentGrid'
import { normalizeString } from '../utils/helpers'
import { safeReadJson, safeWriteJson } from '../utils/storage'
import { getLocations, onLocationsUpdated } from '../utils/contentRepository'

const RARITY_OPTIONS = ['common', 'uncommon', 'rare', 'ultra-rare']
const STATUS_OPTIONS = ['released', 'unreleased']

const sanitizeStoredArray = (key, allowed) => {
  const value = safeReadJson(key, [])
  if (!Array.isArray(value)) return []

  const normalized = value.map((item) => String(item).trim())
  return normalized.filter((item, index) => allowed.includes(item) && normalized.indexOf(item) === index)
}

const LocationsView = ({ language }) => {
  const [locations, setLocations] = useState(() => getLocations())
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRarities, setSelectedRarities] = useState(() =>
    sanitizeStoredArray('selectedRarities_locations', RARITY_OPTIONS)
  )
  const [selectedStatuses, setSelectedStatuses] = useState(() =>
    sanitizeStoredArray('selectedStatuses_locations', STATUS_OPTIONS)
  )
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [filtersOpen, setFiltersOpen] = useState(() =>
    safeReadJson('filtersOpen_locations', false)
  )

  useEffect(() => onLocationsUpdated(setLocations), [])

  useEffect(() => {
    safeWriteJson('selectedRarities_locations', selectedRarities)
  }, [selectedRarities])

  useEffect(() => {
    safeWriteJson('selectedStatuses_locations', selectedStatuses)
  }, [selectedStatuses])

  useEffect(() => {
    safeWriteJson('filtersOpen_locations', filtersOpen)
  }, [filtersOpen])

  const resetFilters = () => {
    setSelectedRarities([])
    setSelectedStatuses([])
    setSortBy('name')
    setSortOrder('asc')
    setSearchTerm('')
  }

  const textFilter = (item) => {
    const normalizedSearch = normalizeString(searchTerm)
    const fieldsToSearch = [
      item.name,
      item.descr,
      item.nameEN,
      item.descrEN,
    ].map((field) => normalizeString(field))

    return fieldsToSearch.some((field) => field.includes(normalizedSearch))
  }

  const rarityOrder = {
    common: 1,
    uncommon: 2,
    rare: 3,
    'ultra-rare': 4,
  }

  const filteredLocations = locations
    .filter((item) => {
      const matchesText = searchTerm ? textFilter(item) : true
      const matchesRarity = selectedRarities.length
        ? selectedRarities.includes(String(item.rarity))
        : true
      const matchesStatus = selectedStatuses.length
        ? selectedStatuses.includes(String(item.status))
        : true
      return matchesText && matchesRarity && matchesStatus
    })
    .sort((a, b) => {
      const modifier = sortOrder === 'asc' ? 1 : -1
      if (sortBy === 'name') {
        const compareField = language === 'EN' ? 'nameEN' : 'name'
        return a[compareField].localeCompare(b[compareField], language) * modifier
      }
      if (sortBy === 'rarity') {
        return (rarityOrder[a.rarity] - rarityOrder[b.rarity]) * modifier
      }
      return 0
    })

  return (
    <>
      <LocationFilters
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        selectedRarities={selectedRarities}
        setSelectedRarities={setSelectedRarities}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        language={language}
      />

      <Search
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        language={language}
      />

      <SortControls
        activeView="locations"
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        resetFilters={resetFilters}
        filteredCount={filteredLocations.length}
        totalCount={locations.length}
        language={language}
      />

      <ContentGrid
        activeView="locations"
        data={filteredLocations}
        language={language}
      />
    </>
  )
}

export default LocationsView
